import * as React from 'react';
import { IgcDialogComponent as Component } from 'igniteui-webcomponents';
import type { IgcDialogComponentEventMap } from 'igniteui-webcomponents';
import { type EventName, createComponent } from '../react-props.js';

/**
 * A modal dialog component built on the native `<dialog>` element.
 *
 * The dialog traps focus while open and blocks interaction with the rest
 * of the page (modal semantics). It supports animated open/close
 * transitions, an optional backdrop overlay, and multiple content areas
 * through named slots.
 *
 * The component integrates with the
 * [Invoker Commands API](https://developer.mozilla.org/en-US/docs/Web/API/Invoker_Commands_API):
 * an Ignite button or a native `<button>` with `command="--show"` / `"--hide"` / `"--toggle"`
 * and `commandfor` pointing to this element will call the corresponding method
 * declaratively without any JavaScript.
 * @slot  - General-purpose content area. Also the target for any `<form method="dialog">` placed inside the dialog.
 * @slot title - Content rendered in the dialog header. Falls back to the `title` attribute when empty.
 * @slot message - A dedicated message/body area rendered above the default slot. Hidden when no content is assigned.
 * @slot footer - Content rendered in the dialog footer. When empty, a default "OK" close button is shown (unless `hide-default-action` is set).
 * @fires igcClosing - Emitted just before the dialog closes. Cancelable — call `event.preventDefault()` to abort the closing sequence.
 * @fires igcClosed - Emitted after the dialog has fully closed and its exit animation has completed.
 * @csspart base - The native `<dialog>` element.
 * @csspart title - The `<header>` element wrapping the title slot.
 * @csspart content - The `<section>` element wrapping the message and default slots.
 * @csspart footer - The `<footer>` element wrapping the footer slot.
 * @csspart backdrop - The decorative backdrop overlay element.
 * @csspart animating - Applied to the backdrop while an animation is running.
 * @class
 */
export const IgrDialog = /* @__PURE__ */ createComponent({
  tagName: 'igc-dialog',
  displayName: 'IgrDialog',
  elementClass: Component,
  react: React,
  events: {
    onClosing: 'igcClosing' as EventName<
      IgcDialogComponentEventMap['igcClosing']
    >,
    onClosed: 'igcClosed' as EventName<IgcDialogComponentEventMap['igcClosed']>,
  },
});

export type IgrDialog = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrDialogModule = Component;
